import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RawIngestion, IngestionDraft, IngestionEventType, ScrapeOptions, SourceType, IngestionStatus, IngestionDataType, RefreshHint, ReviewStatus } from "@/components/admin/types";
import { draftToInsertPayload } from "@/components/admin/types";

// Transform database row to RawIngestion type
function transformIngestion(row: Record<string, unknown>): RawIngestion {
  return {
    id: row.id as string,
    source_type: row.source_type as SourceType,
    source_url: row.source_url as string | null,
    source_file_name: row.source_file_name as string | null,
    source_file_size: row.source_file_size as number | null,
    refresh_hint: row.refresh_hint as RefreshHint | null,
    raw_content: row.raw_content as string,
    raw_html: row.raw_html as string | null,
    content_hash: row.content_hash as string | null,
    scrape_options: row.scrape_options as ScrapeOptions | null,
    status: row.status as IngestionStatus,
    data_type: row.data_type as IngestionDataType | null,
    scrape_error: row.scrape_error as string | null,
    scraped_at: row.scraped_at as string | null,
    structured_extraction: row.structured_extraction as Record<string, unknown> | null,
    ai_notes: row.ai_notes as Record<string, unknown> | null,
    review_status: row.review_status as ReviewStatus | null,
    reviewed_at: row.reviewed_at as string | null,
    reviewed_by: row.reviewed_by as string | null,
    created_by: row.created_by as string,
    created_at: row.created_at as string,
    updated_at: row.updated_at as string,
  };
}

// Fetch all ingestions (admin only)
export function useIngestions() {
  return useQuery({
    queryKey: ["ingestions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("raw_ingestions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(transformIngestion);
    },
  });
}

// Fetch single ingestion by ID
export function useIngestion(id: string | null) {
  return useQuery({
    queryKey: ["ingestions", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("raw_ingestions")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return transformIngestion(data as Record<string, unknown>);
    },
    enabled: !!id,
  });
}

// Helper to log an event
async function logEvent(
  ingestionId: string,
  eventType: IngestionEventType,
  payload: Record<string, unknown> | null,
  userId: string | null
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from("raw_ingestion_events") as any).insert({
    ingestion_id: ingestionId,
    event_type: eventType,
    payload: payload,
    created_by: userId,
  });
  if (error) console.error("Failed to log event:", error);
}

// Save new ingestion + log 'created' event
export function useSaveIngestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ draft, userId }: { draft: IngestionDraft; userId: string }) => {
      const payload = await draftToInsertPayload(draft, userId);
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from("raw_ingestions") as any)
        .insert({
          source_type: payload.source_type,
          source_url: payload.source_url,
          source_file_name: payload.source_file_name,
          source_file_size: payload.source_file_size,
          refresh_hint: payload.refresh_hint,
          raw_content: payload.raw_content,
          raw_html: payload.raw_html,
          content_hash: payload.content_hash,
          scrape_options: payload.scrape_options,
          status: payload.status,
          data_type: payload.data_type,
          scrape_error: payload.scrape_error,
          scraped_at: payload.scraped_at,
          created_by: payload.created_by,
        })
        .select()
        .single();

      if (error) throw error;

      // Log 'created' event
      await logEvent(data.id, "created", {
        source_type: payload.source_type,
        status: payload.status,
        data_type: payload.data_type,
        content_length: payload.raw_content.length,
      }, userId);

      return transformIngestion(data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ingestions"] });
    },
  });
}

// Update existing ingestion + log change events
export function useUpdateIngestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
      userId,
      previousValues,
    }: {
      id: string;
      updates: Partial<RawIngestion>;
      userId: string | null;
      previousValues?: Partial<RawIngestion>;
    }) => {
      // Prepare updates for Supabase (handle jsonb fields)
      const dbUpdates: Record<string, unknown> = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.data_type !== undefined) dbUpdates.data_type = updates.data_type;
      if (updates.review_status !== undefined) dbUpdates.review_status = updates.review_status;
      if (updates.reviewed_at !== undefined) dbUpdates.reviewed_at = updates.reviewed_at;
      if (updates.reviewed_by !== undefined) dbUpdates.reviewed_by = updates.reviewed_by;
      if (updates.raw_content !== undefined) dbUpdates.raw_content = updates.raw_content;
      if (updates.raw_html !== undefined) dbUpdates.raw_html = updates.raw_html;
      if (updates.content_hash !== undefined) dbUpdates.content_hash = updates.content_hash;
      if (updates.scrape_options !== undefined) dbUpdates.scrape_options = updates.scrape_options as object;
      if (updates.scraped_at !== undefined) dbUpdates.scraped_at = updates.scraped_at;
      if (updates.scrape_error !== undefined) dbUpdates.scrape_error = updates.scrape_error;
      if (updates.structured_extraction !== undefined) dbUpdates.structured_extraction = updates.structured_extraction as object;
      if (updates.ai_notes !== undefined) dbUpdates.ai_notes = updates.ai_notes as object;

      const { data, error } = await supabase
        .from("raw_ingestions")
        .update(dbUpdates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // Log change events based on what changed
      if (previousValues) {
        if (updates.status && updates.status !== previousValues.status) {
          await logEvent(id, "status_changed", {
            from: previousValues.status,
            to: updates.status,
          }, userId);
        }
        if (updates.data_type !== undefined && updates.data_type !== previousValues.data_type) {
          await logEvent(id, "data_type_changed", {
            from: previousValues.data_type,
            to: updates.data_type,
          }, userId);
        }
        if (updates.review_status !== undefined && updates.review_status !== previousValues.review_status) {
          await logEvent(id, "review_status_changed", {
            from: previousValues.review_status,
            to: updates.review_status,
          }, userId);
        }
        if (updates.content_hash && updates.content_hash !== previousValues.content_hash) {
          await logEvent(id, "content_updated", {
            old_hash: previousValues.content_hash,
            new_hash: updates.content_hash,
          }, userId);
        }
      }

      return transformIngestion(data as Record<string, unknown>);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ingestions"] });
      queryClient.invalidateQueries({ queryKey: ["ingestions", data.id] });
    },
  });
}

// Log scrape events
export function useLogScrapeEvent() {
  return useMutation({
    mutationFn: async ({
      ingestionId,
      eventType,
      payload,
      userId,
    }: {
      ingestionId: string;
      eventType: 'scrape_started' | 'scrape_completed' | 'scrape_failed';
      payload: Record<string, unknown>;
      userId: string | null;
    }) => {
      await logEvent(ingestionId, eventType, payload, userId);
    },
  });
}

// Delete ingestion
export function useDeleteIngestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("raw_ingestions")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ingestions"] });
    },
  });
}

// Extract ingestion using LLM
export function useExtractIngestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ingestionId: string): Promise<{
      success: boolean;
      structured_extraction: Record<string, unknown>;
      ai_notes: Record<string, unknown>;
    }> => {
      const { data, error } = await supabase.functions.invoke("extract-ingestion", {
        body: { ingestionId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (_, ingestionId) => {
      queryClient.invalidateQueries({ queryKey: ["ingestions"] });
      queryClient.invalidateQueries({ queryKey: ["ingestions", ingestionId] });
      queryClient.invalidateQueries({ queryKey: ["ingestion-events", ingestionId] });
    },
  });
}
